import { useState, useRef } from "react";

const serviceCards = [
  {
    id: 1,
    label: "Special Nursing Home",
    title: "特別養護老人ホーム",
    description: "介護が必要な高齢者が安心して生活できる入居型施設です。24時間体制で専門スタッフが身体介護・生活支援を行います。",
    icon: "ri-home-heart-line",
    image: "https://readdy.ai/api/search-image?query=bright%20warm%20Japanese%20special%20nursing%20home%20room%20with%20comfortable%20bed%20natural%20light%20plants%20elderly%20care%20facility%20interior%20cozy%20atmosphere&width=600&height=400&seq=svc01&orientation=landscape",
    color: "from-green-600 to-green-700",
  },
  {
    id: 2,
    label: "Day Service",
    title: "デイサービス",
    description: "日中に施設へ通い、入浴・食事・リハビリ・レクリエーションなどのサービスを受けられます。在宅生活を続けながらご利用いただけます。",
    icon: "ri-sun-line",
    image: "https://readdy.ai/api/search-image?query=elderly%20people%20enjoying%20group%20activities%20at%20Japanese%20day%20service%20center%20bright%20cheerful%20room%20caregivers%20smiling%20rehabilitation%20exercise&width=600&height=400&seq=svc02&orientation=landscape",
    color: "from-teal-600 to-teal-700",
  },
  {
    id: 3,
    label: "Home Helper",
    title: "ホームヘルパー",
    description: "ヘルパーがご自宅を訪問し、身体介護や生活援助を行います。住み慣れた環境での生活を専門スタッフがサポートします。",
    icon: "ri-user-heart-line",
    image: "https://readdy.ai/api/search-image?query=Japanese%20home%20helper%20caregiver%20visiting%20elderly%20person%20at%20home%20helping%20with%20daily%20tasks%20warm%20friendly%20atmosphere%20natural%20light&width=600&height=400&seq=svc03&orientation=landscape",
    color: "from-emerald-600 to-emerald-700",
  },
  {
    id: 4,
    label: "Short Stay",
    title: "ショートステイ",
    description: "介護者の休息や急な用事の際に、短期間施設に宿泊できるサービスです。ご家族の介護負担を軽減します。",
    icon: "ri-moon-line",
    image: "https://readdy.ai/api/search-image?query=comfortable%20short%20stay%20room%20in%20Japanese%20care%20facility%20clean%20tidy%20bed%20window%20view%20peaceful%20relaxing%20environment%20for%20elderly&width=600&height=400&seq=svc04&orientation=landscape",
    color: "from-green-700 to-green-800",
  },
  {
    id: 5,
    label: "Group Home",
    title: "グループホーム",
    description: "認知症の方が少人数で共同生活を送る施設です。家庭的な雰囲気の中で、スタッフと一緒に日常生活を営みます。",
    icon: "ri-group-line",
    image: "https://readdy.ai/api/search-image?query=small%20cozy%20Japanese%20group%20home%20for%20dementia%20care%20residents%20cooking%20together%20with%20staff%20warm%20kitchen%20living%20room%20homely%20atmosphere&width=600&height=400&seq=svc05&orientation=landscape",
    color: "from-teal-700 to-teal-800",
  },
];

const SearchSection = () => {
  const [current, setCurrent] = useState(0);
  const startX = useRef<number | null>(null);

  const prev = () => setCurrent((c) => (c - 1 + serviceCards.length) % serviceCards.length);
  const next = () => setCurrent((c) => (c + 1) % serviceCards.length);

  const handleTouchStart = (e: React.TouchEvent) => {
    startX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (startX.current === null) return;
    const diff = startX.current - e.changedTouches[0].clientX;
    if (diff > 40) next();
    else if (diff < -40) prev();
    startX.current = null;
  };

  const card = serviceCards[current];

  return (
    <section id="facility" className="py-20 lg:py-32 px-6 bg-[#f5f0e8]">
      <div className="max-w-5xl mx-auto">
        {/* Heading */}
        <div className="text-center mb-12">
          <p className="text-green-700 text-xs font-semibold tracking-widest mb-2">Service</p>
          <h2 className="text-gray-800 text-2xl lg:text-3xl font-bold">サービス一覧</h2>
          <p className="text-gray-500 text-sm mt-3">みどり苑が提供する5つのサービスをご紹介します</p>
        </div>

        {/* Carousel */}
        <div
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {/* Card */}
          <div className="overflow-hidden rounded-3xl">
            <div
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${current * 100}%)` }}
            >
              {serviceCards.map((s) => (
                <div key={s.id} className="w-full shrink-0">
                  <div className="flex flex-col lg:flex-row bg-white rounded-3xl overflow-hidden min-h-[420px]">
                    {/* Image */}
                    <div className="lg:w-1/2 h-56 lg:h-auto relative overflow-hidden">
                      <img
                        src={s.image}
                        alt={s.title}
                        className="w-full h-full object-cover object-top"
                      />
                      <div className={`absolute inset-0 bg-gradient-to-br ${s.color} opacity-30`} />
                    </div>

                    {/* Content */}
                    <div className="lg:w-1/2 p-8 lg:p-12 flex flex-col justify-center">
                      <div className={`w-12 h-12 flex items-center justify-center rounded-2xl bg-gradient-to-br ${s.color} text-white mb-5`}>
                        <i className={`${s.icon} text-xl`} />
                      </div>
                      <p className="text-green-600 text-xs font-semibold tracking-widest mb-2">{s.label}</p>
                      <h3 className="text-gray-800 text-2xl lg:text-3xl font-bold mb-4">{s.title}</h3>
                      <p className="text-gray-600 text-sm lg:text-base leading-relaxed">{s.description}</p>
                      <a
                        href="#contact"
                        className="inline-flex items-center gap-2 mt-8 text-green-700 text-sm font-semibold hover:text-green-800 transition-colors"
                      >
                        このサービスについて相談する
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-arrow-right-line" />
                        </div>
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Dots + Prev/Next row */}
          <div className="flex items-center justify-center gap-4 mt-8">
            <button
              type="button"
              onClick={prev}
              className="w-11 h-11 flex items-center justify-center rounded-full bg-white text-green-700 hover:bg-green-50 transition-colors cursor-pointer shrink-0"
              aria-label="前へ"
            >
              <i className="ri-arrow-left-s-line text-xl" />
            </button>

            <div className="flex items-center gap-2">
              {serviceCards.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setCurrent(i)}
                  className={`h-2 rounded-full transition-all cursor-pointer ${
                    i === current ? "bg-green-600 w-8" : "bg-gray-300 w-2"
                  }`}
                  aria-label={`${i + 1}枚目`}
                />
              ))}
            </div>

            <button
              type="button"
              onClick={next}
              className="w-11 h-11 flex items-center justify-center rounded-full bg-white text-green-700 hover:bg-green-50 transition-colors cursor-pointer shrink-0"
              aria-label="次へ"
            >
              <i className="ri-arrow-right-s-line text-xl" />
            </button>
          </div>

          {/* Counter */}
          <p className="text-center text-gray-400 text-xs font-semibold mt-3 tracking-widest">
            {String(current + 1).padStart(2, "0")} / {String(serviceCards.length).padStart(2, "0")}
          </p>
        </div>
      </div>
    </section>
  );
};

export default SearchSection;
